import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Avatar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Alert,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  LocalOffer,
  ShoppingBag,
  Restaurant,
  MovieCreationOutlined,
  Flight,
  Storefront,
  Devices,
  Checkroom,
  LocalDining,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { url } from '../constants';

const CategorySection = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const carouselRef = useRef(null);
  const autoScrollTimeoutRef = useRef(null);
  const scrollBackTimeoutRef = useRef(null);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const iconMap = {
    'Food & Dining': Restaurant,
    'Shopping & Retail': ShoppingBag,
    'Entertainment': MovieCreationOutlined,
    'Travel': Flight,
    'Services': Storefront,
    'Electronics': Devices,
    'Fashion': Checkroom,
    'Groceries': LocalDining,
    'Movies': MovieCreationOutlined,
    'Events': MovieCreationOutlined,
    'Music': MovieCreationOutlined,
    'Sports': MovieCreationOutlined,
    'Beauty & Wellness': Storefront,
    'Home & Living': Storefront,
    'Books & Media': LocalOffer,
    'Fitness': Storefront,
    'Accessories': Checkroom,
  };

  const colorMap = {
    'Food & Dining': '#FF6B6B',
    'Shopping & Retail': '#4ECDC4',
    'Entertainment': '#FFE66D',
    'Travel': '#95E1D3',
    'Services': '#AA96DA',
    'Electronics': '#FCBAD3',
    'Fashion': '#A8E6CF',
    'Groceries': '#1a7f3a',
    'Movies': '#FFE66D',
    'Events': '#FF9F43',
    'Music': '#FF6B9D',
    'Sports': '#54A0FF',
    'Beauty & Wellness': '#FFB6C1',
    'Home & Living': '#DDA15E',
    'Books & Media': '#B5E48C',
    'Fitness': '#FF6B9D',
    'Accessories': '#FFD93D',
  };

  // Load categories from localStorage on component mount
  useEffect(() => {
    const savedCategories = localStorage.getItem('categories');
    if (savedCategories) {
      try {
        const parsedCategories = JSON.parse(savedCategories);
        if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
          console.log('💾 Loaded categories from localStorage:', parsedCategories);
          setCategories(parsedCategories);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('❌ Error parsing saved categories:', error);
        localStorage.removeItem('categories');
      }
    }
    // If no valid cached data, fetch from API
    fetchCategories();
  }, []);

  useEffect(() => {
    checkScroll();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', handleCarouselScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        carousel.removeEventListener('scroll', handleCarouselScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [categories]); // Depend on categories instead of loading

  useEffect(() => {
    if (!loading && categories.length > 0) {
      autoScrollTimeoutRef.current = setTimeout(() => {
        autoScrollCarousel();
      }, 1000);

      return () => {
        clearTimeout(autoScrollTimeoutRef.current);
        clearTimeout(scrollBackTimeoutRef.current);
      };
    }
  }, [loading, categories, isMobile, isTablet]);

  const fetchCategories = async () => {
    setLoading(true);
    setError('');
  
    try {
      console.log('📡 Fetching categories from API...');
      const response = await fetch(`${url}/users/get_categories`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch categories`);
      }
      
      const data = await response.json();
      console.log('✅ Categories fetched successfully:', data);
      
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('No categories found');
      }
      
      // ✅ Save to localStorage with timestamp
      const categoriesWithTimestamp = data.map(category => ({
        ...category,
        cachedAt: Date.now()
      }));
      
      localStorage.setItem('categories', JSON.stringify(categoriesWithTimestamp));
      console.log('💾 Categories saved to localStorage');
      
      setCategories(categoriesWithTimestamp);
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      setError(error.message || 'Failed to load categories. Please try again later.');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const checkScroll = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const handleCarouselScroll = () => {
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
      autoScrollTimeoutRef.current = null;
    }
    if (scrollBackTimeoutRef.current) {
      clearTimeout(scrollBackTimeoutRef.current);
      scrollBackTimeoutRef.current = null;
    }

    checkScroll();

    const carousel = carouselRef.current;
    if (carousel) {
      carousel.removeEventListener('scroll', handleCarouselScroll);
    }
  };

  const scrollCarousel = (direction) => {
    const scrollAmount = 300;
    if (direction === 'left') {
      carouselRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    } else {
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }
    setTimeout(checkScroll, 300);
  };

  const autoScrollCarousel = () => {
    if (!carouselRef.current) return;

    let scrollAmount = 0;
    
    if (isMobile) {
      scrollAmount = carouselRef.current.clientWidth * 0.7;
    } else if (isTablet) {
      scrollAmount = carouselRef.current.clientWidth * 0.7;
    } else {
      scrollAmount = carouselRef.current.clientWidth * 0.45;
    }

    carouselRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });

    const updateScrollStateTimeout = setTimeout(() => {
      if (carouselRef.current) {
        checkScroll();
        
        scrollBackTimeoutRef.current = setTimeout(() => {
          if (carouselRef.current) {
            carouselRef.current.scrollBy({
              left: -scrollAmount,
              behavior: 'smooth',
            });
            
            setTimeout(checkScroll, 300);
          }
        }, 1000);
      }
    }, 300);

    return () => {
      clearTimeout(updateScrollStateTimeout);
      clearTimeout(scrollBackTimeoutRef.current);
    };
  };

  const getIcon = (categoryName) => {
    const Icon = iconMap[categoryName] || LocalOffer;
    return isMobile ? (
      <Icon sx={{ fontSize: 24 }} />
    ) : (
      <Icon sx={{ fontSize: 32 }} />
    );
  };

  const getCategoryColor = (categoryName, storedColor) => {
    return storedColor || colorMap[categoryName] || '#1a7f3a';
  };

  const handleCategoryClick = (category) => {
    console.log('📂 Category clicked:', category);
    
    // Safety check
    if (!category) {
      console.error('❌ Category is undefined');
      return;
    }
    
    // Check if cate_id exists
    if (category.cate_id !== undefined && category.cate_id !== null) {
      console.log('📂 Storing category ID:', category.cate_id, 'Name:', category.cate_name);
      localStorage.setItem('selectedDealCategoryId', String(category.cate_id));
      localStorage.setItem('selectedDealCategory', category.cate_name);
    } else {
      console.warn('⚠️ No cate_id found, using category name only');
      localStorage.setItem('selectedDealCategory', category.cate_name || '');
    }
    
    // Navigate to deals page
    navigate('/deals');
  };

  // Clear cache if older than 24 hours
  useEffect(() => {
    const savedCategories = localStorage.getItem('categories');
    if (savedCategories) {
      try {
        const parsed = JSON.parse(savedCategories);
        const cacheAge = Date.now() - (parsed[0]?.cachedAt || 0);
        const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
        
        if (cacheAge > CACHE_DURATION) {
          console.log('🧹 Clearing stale category cache');
          localStorage.removeItem('categories');
          fetchCategories();
        }
      } catch (error) {
        localStorage.removeItem('categories');
      }
    }
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchCategories}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (categories.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="info">
          No categories available at the moment.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: isMobile ? 3 : 6 }}>
      <Box sx={{ textAlign: 'center', mb: isMobile ? 2 : 4 }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          gutterBottom
          fontWeight={700}
          sx={{ color: '#4B5563' }}
        >
          Browse by Category
        </Typography>
        <Typography 
          variant={isMobile ? 'body2' : 'body1'} 
          color="text.secondary"
        >
          Explore deals across different categories
        </Typography>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {!isMobile && (
          <IconButton
            onClick={() => scrollCarousel('left')}
            disabled={!canScrollLeft}
            sx={{
              position: 'absolute',
              left: -24,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              bgcolor: 'white',
              color: '#1a7f3a',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'all 0.3s ease',
              '&:hover:not(:disabled)': {
                bgcolor: '#1a7f3a',
                color: 'white',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 6px 16px rgba(26, 127, 58, 0.3)',
              },
              '&:disabled': {
                opacity: 0.3,
                cursor: 'not-allowed',
              },
            }}
          >
            <ChevronLeft />
          </IconButton>
        )}

        <Box
          ref={carouselRef}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            gap: isMobile ? 1 : 2,
            pb: 1,
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {isMobile &&
            categories.map((category) => (
              <Box
                key={category.cate_id}
                sx={{
                  flex: '0 0 calc(33.333% - 6px)',
                  minWidth: 'calc(33.333% - 6px)',  
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    transition: 'all 0.3s ease',
                    backgroundColor: '#FFFFFF',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:active': {
                      transform: 'scale(0.96)',
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
                      borderColor: getCategoryColor(category.cate_name, category.cate_color),
                    },
                  }}
                  onClick={() => handleCategoryClick(category)}
                >
                  <Avatar
                    sx={{
                      bgcolor: getCategoryColor(category.cate_name, category.cate_color) + '20',
                      color: getCategoryColor(category.cate_name, category.cate_color),
                      width: 40,
                      height: 40,
                      margin: '0 auto',
                      mb: 0.8,
                    }}
                  >
                    {getIcon(category.cate_name)}
                  </Avatar>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    gutterBottom
                    sx={{ 
                      color: '#4B5563', 
                      fontSize: '0.7rem',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {category.cate_name}
                  </Typography>
                </Paper>
              </Box>
            ))}

          {!isMobile &&
            categories.map((category) => (
              <Box
                key={category.cate_id}
                sx={{
                  flex: isTablet ? '0 0 calc(33.333% - 12px)' : '0 0 calc(20% - 12px)',
                  minWidth: isTablet ? 'calc(33.333% - 12px)' : 'calc(20% - 12px)',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    backgroundColor: '#FFFFFF',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
                      borderColor: getCategoryColor(category.cate_name, category.cate_color),
                    },
                  }}
                  onClick={() => handleCategoryClick(category)}
                >
                  <Avatar
                    sx={{
                      bgcolor: getCategoryColor(category.cate_name, category.cate_color) + '20',
                      color: getCategoryColor(category.cate_name, category.cate_color),
                      width: 60,
                      height: 60,
                      margin: '0 auto',
                      mb: 2,
                    }}
                  >
                    {getIcon(category.cate_name)}
                  </Avatar>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    gutterBottom
                    sx={{ color: '#4B5563' }}
                  >
                    {category.cate_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Explore deals
                  </Typography>
                </Paper>
              </Box>
            ))}
        </Box>

        {!isMobile && (
          <IconButton
            onClick={() => scrollCarousel('right')}
            disabled={!canScrollRight}
            sx={{
              position: 'absolute',
              right: -24,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              bgcolor: 'white',
              color: '#1a7f3a',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'all 0.3s ease',
              '&:hover:not(:disabled)': {
                bgcolor: '#1a7f3a',
                color: 'white',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 6px 16px rgba(26, 127, 58, 0.3)',
              },
              '&:disabled': {
                opacity: 0.3,
                cursor: 'not-allowed',
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>
    </Container>
  );
};

export default CategorySection;
